import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as jobAssignmentApi from '@company/services/jobAssignment/api';
import QueryKeys from '@constants/queryKeys';

interface ShareJobParams {
  jobId: number;
  workerId: number;
  onSuccess?: () => void;
  showSuccessAlert: (message: string) => void;
  showWarningAlert: (message: string) => void;
}

export function useShareJobMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      workerId,
      onSuccess,
      showSuccessAlert,
      showWarningAlert,
    }: ShareJobParams) => {
      const result = await jobAssignmentApi.shareJobToWorker(jobId, workerId);
      return { result, onSuccess, showSuccessAlert, showWarningAlert };
    },
    onSuccess: ({ result, onSuccess, showSuccessAlert, showWarningAlert }) => {
      console.log('ShareJob mutation onSuccess called with result:', result);
      if (result?.success) {
        console.log('Showing success alert with message:', result.message);
        showSuccessAlert(result.message);
        // Invalidate active jobs queries for all workers
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.COMPANY_JOBS, 'active'],
        });
        // Also invalidate worker profile data
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.WORKER_PROFILE],
        });
        // Call the custom success callback if provided
        onSuccess?.();
      } else {
        console.log('Result not successful, showing warning alert');
        showWarningAlert(
          `An error has occurred while trying to share job with worker`
        );
      }
    },
    onError: (error, variables) => {
      console.error('Error sharing job:', error);
      variables.showWarningAlert('Failed to share job with worker');
    },
  });
}

export default useShareJobMutation;
